import React, { useState } from 'react';
import { XIcon, UserPlusIcon, MailIcon, MessageSquareIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ShareWithTeachersModalProps {
  assessment: any;
  onClose: () => void;
  onShare: (emails: string[], message: string) => void;
}
export function ShareWithTeachersModal({
  assessment,
  onClose,
  onShare
}: ShareWithTeachersModalProps) {
  const [emails, setEmails] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const handleShare = () => {
    // Basic email validation
    const emailList = emails.split(',').map(e => e.trim()).filter(e => e);
    if (emailList.length === 0) {
      setEmailError('Please enter at least one email address');
      return;
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      setEmailError(`Invalid email(s): ${invalidEmails.join(', ')}`);
      return;
    }
    setEmailError('');
    onShare(emailList, message);
  };
  return <AnimatePresence data-id="element-2207">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2208">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="bg-surface-elevated rounded-2xl shadow-2xl max-w-2xl w-full border border-custom" onClick={e => e.stopPropagation()} data-id="element-2209">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-custom" data-id="element-2210">
            <div className="flex items-center gap-3" data-id="element-2211">
              <div className="w-10 h-10 bg-[#2563EB] bg-opacity-10 rounded-xl flex items-center justify-center" data-id="element-2212">
                <UserPlusIcon size={20} className="text-[#2563EB]" data-id="element-2213" />
              </div>
              <div data-id="element-2214">
                <h2 className="text-xl font-bold text-text-primary" data-id="element-2215">
                  Share with Teachers
                </h2>
                <p className="text-sm text-text-secondary" data-id="element-2216">
                  Invite colleagues to review this assessment
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors" aria-label="Close modal" data-id="element-2217">
              <XIcon size={20} className="text-text-secondary" data-id="element-2218" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6" data-id="element-2219">
            {/* Assessment Info */}
            <div className="bg-surface rounded-xl p-4 border border-custom" data-id="element-2220">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1" data-id="element-2221">
                Assessment
              </p>
              <p className="text-sm font-semibold text-text-primary" data-id="element-2222">
                {assessment.title}
              </p>
            </div>

            {/* Email Input */}
            <div data-id="element-2223">
              <label className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-2" data-id="element-2224">
                <MailIcon size={16} data-id="element-2225" />
                Teacher Email Addresses
              </label>
              <input type="text" placeholder="Enter email addresses separated by commas" value={emails} onChange={e => {
              setEmails(e.target.value);
              setEmailError('');
            }} className="w-full px-4 py-3 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary" data-id="element-2226" />
              {emailError && <p className="text-xs text-red-600 mt-2" data-id="element-2227">{emailError}</p>}
              <p className="text-xs text-text-tertiary mt-2" data-id="element-2228">
                Separate multiple emails with commas (e.g., teacher1@school.com,
                teacher2@school.com)
              </p>
            </div>

            {/* Message Input */}
            <div data-id="element-2229">
              <label className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-2" data-id="element-2230">
                <MessageSquareIcon size={16} data-id="element-2231" />
                Message (Optional)
              </label>
              <textarea placeholder="Add a personal message to your invitation..." value={message} onChange={e => setMessage(e.target.value)} rows={4} className="w-full px-4 py-3 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary resize-none" data-id="element-2232" />
            </div>

            {/* Permission Info */}
            <div className="bg-[#2563EB] bg-opacity-5 border border-[#2563EB] border-opacity-20 rounded-lg p-4" data-id="element-2233">
              <p className="text-xs font-semibold text-[#2563EB] mb-1" data-id="element-2234">
                What teachers can do:
              </p>
              <ul className="text-xs text-text-secondary space-y-1" data-id="element-2235">
                <li data-id="element-2236">• View the assessment and all questions</li>
                <li data-id="element-2237">• Provide feedback and suggestions</li>
                <li data-id="element-2238">• Cannot edit or publish the assessment</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-custom" data-id="element-2239">
            <button onClick={onClose} className="px-6 py-2.5 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface transition-colors" data-id="element-2240">
              Cancel
            </button>
            <button onClick={handleShare} className="px-6 py-2.5 bg-[#00B75F] text-white rounded-lg font-semibold hover:bg-[#00a055] transition-colors shadow-sm flex items-center gap-2" data-id="element-2241">
              <UserPlusIcon size={18} data-id="element-2242" />
              Send Invitation
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}